// /projects/:id — the "what's the status of this project?" page.
//
// 30-second answer at the top: financial summary with horizontal bar +
// ask / committed / gap / available numbers + partner caption + target
// date countdown.
//
// Below: attached applications (mini-kanban scoped to this project),
// matching grants pane (ranked, with cited reasoning disclosure),
// activity timeline.
//
// Edit drawer right-slides, mirrors the /partners pattern. Re-embed on
// description change is handled server-side; UI surfaces an inline note
// so Lisa knows to refresh matches after description edits.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import {
  formatCAD,
  formatCADCompact,
  formatDate,
  formatCountdown,
  countdownToneCls,
  projectStatusMeta,
  type ProjectStatus,
  moneyToneCls,
  stageMeta,
  type AppStage,
  KANBAN_STAGES,
} from '../lib/format';
import { pushToast } from '../hooks/useToasts';
import { PageFooter } from '../components/PageFooter';

interface ProjectDetail {
  id: string;
  partner_profile_id: string;
  partner_name: string;
  partner_slug: string;
  partner_matching_funds_ceiling: number | null;
  name: string;
  description: string | null;
  ask_cad: number | null;
  match_available_cad: number | null;
  target_funding_date: number | null;
  status: ProjectStatus;
  brief_text: string | null;
  source_quotes: Record<string, string | null> | null;
  created_at: number;
  updated_at: number;
  created_by: string | null;
  applications: AppRow[];
  matches: MatchRow[];
  financial_summary: {
    ask_cad: number;
    committed_cad: number;
    gap_cad: number;
    match_available_cad: number | null;
    application_count: number;
    match_count: number;
  };
}

interface AppRow {
  id: string;
  match_id: string;
  grant_program_id: string;
  funding_ask_cad: number;
  match_committed_cad: number;
  stage: AppStage;
  submitted_at: number | null;
  decision_at: number | null;
  awarded_amount_cad: number | null;
  created_at: number;
  grant_title: string;
  grant_source_url: string;
  grant_closes_at: number | null;
  funder_name: string;
  funder_level: string;
}

interface MatchRow {
  id: string;
  grant_program_id: string;
  fit_score: number;
  eligibility_verdict: 'eligible' | 'likely_eligible' | 'unclear' | 'probably_not' | 'not_eligible';
  status: 'new' | 'watching' | 'pursuing' | 'dismissed' | 'escalated' | 'applied';
  surfaced_at: number;
  status_updated_at: number | null;
  grant_title: string;
  grant_source_url: string;
  grant_closes_at: number | null;
  funder_name: string;
  funder_level: string;
  funding_min_cad: number | null;
  funding_max_cad: number | null;
  intake_type: string | null;
}

export default function ProjectDetailPage() {
  const { id } = useParams({ from: '/projects/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [openEdit, setOpenEdit] = useState(false);
  const [descChangedSinceMatch, setDescChangedSinceMatch] = useState(false);

  const detail = useQuery({
    queryKey: ['project', id],
    queryFn: () => api.get<ProjectDetail>(`/projects/${id}`),
  });

  // Detail also drives matches; we'll trigger refresh after a fresh match run.
  const matchMut = useMutation({
    mutationFn: () => api.post(`/projects/${id}/match`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', id] });
      setDescChangedSinceMatch(false);
      pushToast('Matches refreshed', 'success', 1500);
    },
    onError: (err) => pushToast(err instanceof ApiError ? err.message : 'Match run failed', 'error'),
  });

  const archiveMut = useMutation({
    mutationFn: () =>
      api.post<{ id: string; status: string; archived_at_ms: number }>(`/projects/${id}/archive`),
    onSuccess: (res, _vars, _ctx) => {
      qc.invalidateQueries({ queryKey: ['project', id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      const archivedAtMs = res.data.archived_at_ms;
      const projectName = detail.data?.data.name ?? 'project';
      const priorStatus = detail.data?.data.status;
      // Undoable archive: 10s window, single Undo affordance in the toast.
      // Per Phase 3 note #1 — never confirm a reversible destructive; surface
      // the action with an Undo affordance instead.
      pushToast(
        `✓ Archived ${projectName}`,
        'success',
        10_000,
        {
          label: 'Undo',
          onClick: () => {
            archiveUndoMut.mutate({ archivedAtMs, priorStatus });
          },
        },
      );
    },
    onError: (err) => pushToast(err instanceof ApiError ? err.message : 'Archive failed', 'error'),
  });

  const archiveUndoMut = useMutation({
    mutationFn: ({ archivedAtMs, priorStatus }: { archivedAtMs: number; priorStatus?: ProjectStatus }) =>
      api.post(`/projects/${id}/archive-undo`, {
        archived_at_ms: archivedAtMs,
        ...(priorStatus && priorStatus !== 'cancelled' ? { restore_status: priorStatus } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      pushToast('Restored', 'success', 1500);
    },
    onError: (err) => pushToast(err instanceof ApiError ? err.message : 'Undo failed — refresh to check status', 'error'),
  });

  const matchStatusMut = useMutation({
    mutationFn: ({ matchId, status }: { matchId: string; status: 'pursuing' | 'watching' | 'dismissed' }) =>
      api.patch(`/project-matches/${matchId}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); },
    onError: (err) => pushToast(err instanceof ApiError ? err.message : 'Status change failed', 'error'),
  });

  if (detail.error instanceof ApiError && detail.error.status === 401) {
    navigate({ to: '/welcome' });
    return null;
  }
  if (detail.error instanceof ApiError && detail.error.status === 404) {
    return (
      <div className="p-6 text-sm">
        <p className="text-neutral-800">Project not found.</p>
        <Link to="/projects" className="text-accent underline">← Back to projects</Link>
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="p-6 text-sm text-neutral-600">Loading project — a moment.</div>
    );
  }

  const p = detail.data?.data;
  if (!p) return null;

  const fs = p.financial_summary;
  const countdown = formatCountdown(p.target_funding_date);
  const status = projectStatusMeta[p.status];

  const askCad = fs.ask_cad;
  const committedPct = askCad > 0 ? Math.min(100, Math.round((fs.committed_cad / askCad) * 100)) : 0;

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1100px] mx-auto px-5 py-5">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-xs text-neutral-500 mb-3">
            <Link to="/projects" className="hover:text-neutral-700 underline-offset-2 hover:underline">Projects</Link>
            <span className="mx-1">›</span>
            <Link to="/partners" className="hover:text-neutral-700 underline-offset-2 hover:underline">{p.partner_name}</Link>
            <span className="mx-1">›</span>
            <span className="text-neutral-900">{p.name}</span>
          </nav>

          {/* Header */}
          <header className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-semibold text-neutral-900">{p.name}</h1>
                <span className={'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ' + status.cls}>{status.label}</span>
                {countdown && p.target_funding_date && (
                  <span className={'text-[10px] px-1.5 py-0.5 rounded ' + countdownToneCls[countdown.tone]}>
                    Target {formatDate(p.target_funding_date)} · {countdown.text}
                  </span>
                )}
              </div>
              {p.description && (
                <p className="mt-1 text-sm text-neutral-700 leading-relaxed max-w-3xl">{p.description}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setOpenEdit(true)}
                className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
              >Edit <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded ml-1">E</kbd></button>
              {p.status !== 'cancelled' && (
                <button
                  type="button"
                  onClick={() => archiveMut.mutate()}
                  disabled={archiveMut.isPending}
                  className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50 text-neutral-700 disabled:opacity-50"
                >Archive</button>
              )}
            </div>
          </header>

          {/* Financial summary */}
          <FinancialSummaryCard
            ask={askCad}
            committed={fs.committed_cad}
            gap={fs.gap_cad}
            matchAvailable={fs.match_available_cad}
            partnerName={p.partner_name}
            partnerCeiling={p.partner_matching_funds_ceiling}
            applicationCount={fs.application_count}
            committedPct={committedPct}
          />

          {/* Re-embed note */}
          {descChangedSinceMatch && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-3">
              <span>
                <span className="font-medium">Description changed</span> — refresh matches to see updated results.
              </span>
              <button
                type="button"
                onClick={() => matchMut.mutate()}
                disabled={matchMut.isPending}
                className="ml-auto text-xs rounded-md bg-amber-200 text-amber-950 px-2 py-1 hover:bg-amber-300 disabled:opacity-50"
              >Refresh now</button>
            </div>
          )}

          {/* Two-column section: applications + matches */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6">
            <section aria-labelledby="apps-heading">
              <h2 id="apps-heading" className="text-sm font-semibold text-neutral-900 mb-2">
                Attached applications <span className="text-neutral-500 font-normal">({p.applications.length})</span>
              </h2>
              {p.applications.length === 0 ? (
                <div className="rounded-md border border-neutral-200 bg-neutral-50/40 p-3 text-xs text-neutral-700">
                  No applications yet. Pursue a match below to create the first one.
                </div>
              ) : (
                <MiniKanban applications={p.applications} />
              )}
            </section>

            <section aria-labelledby="matches-heading">
              <div className="flex items-center justify-between mb-2">
                <h2 id="matches-heading" className="text-sm font-semibold text-neutral-900">
                  Matching grants <span className="text-neutral-500 font-normal">({p.matches.length})</span>
                </h2>
                <button
                  type="button"
                  onClick={() => matchMut.mutate()}
                  disabled={matchMut.isPending}
                  className="text-xs rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {matchMut.isPending ? 'Searching — ~5s…' : 'Refresh matches'}
                  <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded">M</kbd>
                </button>
              </div>
              {p.matches.length === 0 ? (
                <div className="rounded-md border border-neutral-200 bg-neutral-50/40 p-3 text-xs text-neutral-700 leading-relaxed">
                  No matches yet. Click <span className="font-medium">Refresh matches</span> to search verified grants now, or wait for the overnight cron. Granted only matches against grants a human has approved in <Link to="/admin/verify" className="underline text-accent">Verification</Link>.
                </div>
              ) : (
                <ul className="space-y-2">
                  {p.matches.map((m) => (
                    <li key={m.id}>
                      <ProjectMatchCard
                        match={m}
                        onPursue={() => matchStatusMut.mutate({ matchId: m.id, status: 'pursuing' })}
                        onWatch={() => matchStatusMut.mutate({ matchId: m.id, status: 'watching' })}
                        onDismiss={() => matchStatusMut.mutate({ matchId: m.id, status: 'dismissed' })}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Activity timeline (derived) */}
          <ActivityTimeline project={p} />
        </div>
      </div>

      <PageFooter />

      {openEdit && (
        <EditProjectDrawer
          project={p}
          onClose={() => setOpenEdit(false)}
          onDescChanged={() => setDescChangedSinceMatch(true)}
        />
      )}
    </div>
  );
}

// ── Financial summary card ───────────────────────────────────────────────

function FinancialSummaryCard({
  ask, committed, gap, matchAvailable, partnerName, partnerCeiling, applicationCount, committedPct,
}: {
  ask: number;
  committed: number;
  gap: number;
  matchAvailable: number | null;
  partnerName: string;
  partnerCeiling: number | null;
  applicationCount: number;
  committedPct: number;
}) {
  return (
    <section
      aria-labelledby="financial-summary-heading"
      className="rounded-md border border-neutral-200 bg-white p-4"
    >
      <h2 id="financial-summary-heading" className="sr-only">Financial summary</h2>
      {ask > 0 ? (
        <div
          role="progressbar"
          aria-label={`Project funding progress: ${committedPct}% committed against ask`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={committedPct}
          className="h-3 bg-neutral-200 rounded overflow-hidden"
        >
          <div className={'h-full ' + (gap === 0 ? 'bg-emerald-600' : 'bg-accent')} style={{ width: `${committedPct}%` }} />
        </div>
      ) : (
        <p className="text-xs text-neutral-500 italic">No ask amount set. Edit the project to add one.</p>
      )}
      <dl className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 text-sm">
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-neutral-500">Ask</dt>
          <dd className="font-semibold text-neutral-900 tabular-nums">{formatCAD(ask)}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-neutral-500">
            Committed <span className="text-neutral-400 normal-case font-normal">via {applicationCount} app{applicationCount === 1 ? '' : 's'}</span>
          </dt>
          <dd className="font-semibold text-neutral-900 tabular-nums">{formatCAD(committed)}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-neutral-500">Gap</dt>
          <dd className={'font-semibold tabular-nums ' + moneyToneCls(gap, 'gap')}>{formatCAD(gap)}</dd>
        </div>
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-neutral-500">Match available</dt>
          <dd className="tabular-nums">
            <span className={'font-semibold ' + moneyToneCls(matchAvailable ?? 0, 'available')}>{formatCAD(matchAvailable)}</span>
            <span className="block text-[11px] text-neutral-500 font-normal mt-0.5">{partnerName}{partnerCeiling != null ? ` · ceiling ${formatCADCompact(partnerCeiling)}` : ''}</span>
          </dd>
        </div>
      </dl>
    </section>
  );
}

// ── Mini kanban (scoped to this project) ─────────────────────────────────

function MiniKanban({ applications }: { applications: AppRow[] }) {
  const byStage = useMemo(() => {
    const m: Record<AppStage, AppRow[]> = {
      drafting: [], submitted: [], awaiting: [], won: [], lost: [], withdrawn: [],
    };
    for (const a of applications) (m[a.stage] ?? []).push(a);
    return m;
  }, [applications]);
  return (
    <div className="grid grid-cols-5 gap-1.5">
      {KANBAN_STAGES.map((s) => {
        const meta = stageMeta[s];
        return (
          <div key={s} className={'rounded-md border min-h-[120px] ' + meta.columnCls}>
            <div className="px-2 py-1 border-b border-inherit flex items-center justify-between text-[10px]">
              <span className={'uppercase tracking-wide px-1 py-0.5 rounded ' + meta.cls}>{meta.label}</span>
              <span className="text-neutral-600">{byStage[s].length}</span>
            </div>
            <ul className="p-1.5 space-y-1">
              {byStage[s].map((a) => (
                <li key={a.id}>
                  <Link
                    to="/applications/$id"
                    params={{ id: a.id }}
                    className="block bg-white border border-neutral-200 rounded px-1.5 py-1 text-[11px] hover:border-neutral-300"
                  >
                    <div className="font-medium text-neutral-900 line-clamp-2 leading-snug">{a.grant_title}</div>
                    <div className="mt-0.5 text-[10px] text-neutral-600 tabular-nums">{formatCADCompact(a.funding_ask_cad)}</div>
                  </Link>
                </li>
              ))}
              {byStage[s].length === 0 && (
                <li className="text-[10px] text-neutral-500 px-1 py-0.5">—</li>
              )}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// ── Match card on project detail ─────────────────────────────────────────

function ProjectMatchCard({
  match, onPursue, onWatch, onDismiss,
}: {
  match: MatchRow;
  onPursue: () => void;
  onWatch: () => void;
  onDismiss: () => void;
}) {
  const countdown = formatCountdown(match.grant_closes_at);
  const fitPct = Math.round(match.fit_score * 100);
  const verdictColor =
    match.eligibility_verdict === 'eligible' ? 'bg-emerald-700 text-white' :
    match.eligibility_verdict === 'likely_eligible' ? 'bg-amber-300 text-amber-950' :
    match.eligibility_verdict === 'unclear' ? 'bg-neutral-600 text-white' :
    match.eligibility_verdict === 'probably_not' ? 'bg-rose-200 text-rose-900' :
    'bg-rose-700 text-white';

  return (
    <div className={
      'border rounded-md p-2.5 bg-white ' +
      (match.status === 'pursuing' ? 'border-accent bg-accent/5' : 'border-neutral-200')
    }>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-neutral-900">{match.grant_title}</div>
          <div className="text-[11px] text-neutral-600 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{match.funder_name}</span>
            <span className="uppercase tracking-wide">{match.funder_level}</span>
            {countdown && (
              <span className={'px-1.5 py-0.5 rounded text-[10px] ' + countdownToneCls[countdown.tone]}>
                Closes {countdown.text}
              </span>
            )}
            <span className="ml-auto text-neutral-700 tabular-nums">
              {formatCADCompact(match.funding_min_cad)} – {formatCADCompact(match.funding_max_cad)}
            </span>
          </div>
        </div>
        <div className="shrink-0 text-right text-xs">
          <div className="font-medium text-neutral-900">{fitPct}%</div>
          <span className={'inline-block text-[10px] px-1 py-0.5 rounded mt-0.5 ' + verdictColor}>
            {match.eligibility_verdict.replace('_', ' ')}
          </span>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-xs">
        <button
          type="button"
          onClick={onPursue}
          disabled={match.status === 'pursuing'}
          className={'rounded px-2 py-0.5 text-[11px] ' + (
            match.status === 'pursuing'
              ? 'bg-accent text-white'
              : 'border border-neutral-300 hover:bg-neutral-50'
          )}
        >{match.status === 'pursuing' ? '✓ Pursuing' : 'Pursue'} <kbd className="font-mono text-[9px] ml-0.5 opacity-80">P</kbd></button>
        <button
          type="button"
          onClick={onWatch}
          disabled={match.status === 'watching'}
          className={'rounded px-2 py-0.5 text-[11px] ' + (
            match.status === 'watching'
              ? 'bg-sky-200 text-sky-900'
              : 'border border-neutral-300 hover:bg-neutral-50'
          )}
        >Watch <kbd className="font-mono text-[9px] ml-0.5 opacity-80">W</kbd></button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={match.status === 'dismissed'}
          className="rounded border border-neutral-300 px-2 py-0.5 text-[11px] text-neutral-600 hover:bg-neutral-50"
        >Dismiss <kbd className="font-mono text-[9px] ml-0.5 opacity-80">N</kbd></button>
        <a
          href={match.grant_source_url}
          target="_blank"
          rel="noreferrer noopener"
          className="ml-auto text-accent underline text-[11px]"
        >Funder page ↗</a>
      </div>
    </div>
  );
}

// ── Activity timeline (derived) ──────────────────────────────────────────

function ActivityTimeline({ project }: { project: ProjectDetail }) {
  // Derive events from project + applications + matches. Newest first.
  const events: Array<{ at: number; text: string }> = [];
  events.push({ at: project.created_at, text: `Project created${project.brief_text ? ' from a brief' : ''}` });
  for (const m of project.matches) {
    events.push({ at: m.surfaced_at, text: `Match surfaced — ${m.grant_title}` });
    if (m.status_updated_at && m.status_updated_at !== m.surfaced_at && m.status !== 'new') {
      events.push({ at: m.status_updated_at, text: `Match ${m.status} — ${m.grant_title}` });
    }
  }
  for (const a of project.applications) {
    events.push({ at: a.created_at, text: `Application created — ${a.grant_title}` });
    if (a.submitted_at) events.push({ at: a.submitted_at, text: `Submitted — ${a.grant_title}` });
    if (a.decision_at) events.push({ at: a.decision_at, text: `Decision — ${a.grant_title}` });
  }
  events.sort((a, b) => b.at - a.at);
  if (events.length === 0) return null;
  return (
    <section className="mt-8" aria-labelledby="activity-heading">
      <h2 id="activity-heading" className="text-sm font-semibold text-neutral-900 mb-2">Activity</h2>
      <ol className="border-l-2 border-neutral-200 ml-2 space-y-3">
        {events.slice(0, 20).map((e, i) => (
          <li key={i} className="pl-3 relative">
            <span aria-hidden="true" className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-neutral-400" />
            <div className="text-xs text-neutral-700">{e.text}</div>
            <div className="text-[10px] text-neutral-500">{formatDate(e.at)}</div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// ── Edit drawer ──────────────────────────────────────────────────────────

interface DraftProject {
  name: string;
  description: string;
  ask_cad: number | null;
  match_available_cad: number | null;
  target_funding_date: number | null;
  status: ProjectStatus;
}

function EditProjectDrawer({
  project, onClose, onDescChanged,
}: {
  project: ProjectDetail;
  onClose: () => void;
  onDescChanged: () => void;
}) {
  const qc = useQueryClient();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<DraftProject>({
    name: project.name,
    description: project.description ?? '',
    ask_cad: project.ask_cad,
    match_available_cad: project.match_available_cad,
    target_funding_date: project.target_funding_date,
    status: project.status,
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    dialogRef.current?.querySelector<HTMLInputElement>('input[name="name"]')?.focus();
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        if (dirty) save.mutate();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  const save = useMutation({
    mutationFn: () => {
      const patch: Record<string, unknown> = {};
      if (draft.name !== project.name) patch.name = draft.name;
      const newDesc = draft.description.trim() || null;
      if (newDesc !== project.description) patch.description = newDesc;
      if (draft.ask_cad !== project.ask_cad) patch.ask_cad = draft.ask_cad;
      if (draft.match_available_cad !== project.match_available_cad) patch.match_available_cad = draft.match_available_cad;
      if (draft.target_funding_date !== project.target_funding_date) patch.target_funding_date = draft.target_funding_date;
      if (draft.status !== project.status) patch.status = draft.status;
      if (Object.keys(patch).length === 0) return Promise.resolve({ data: project });
      return api.patch(`/projects/${project.id}`, patch);
    },
    onSuccess: () => {
      pushToast(`Saved ${project.name}`, 'success', 1500);
      qc.invalidateQueries({ queryKey: ['project', project.id] });
      qc.invalidateQueries({ queryKey: ['projects'] });
      const descChanged = (draft.description.trim() || null) !== project.description;
      if (descChanged) onDescChanged();
      setDirty(false);
      onClose();
    },
    onError: (err) => {
      pushToast(err instanceof ApiError ? err.message : 'Save failed — keep your changes and retry', 'error');
    },
  });

  const update = <K extends keyof DraftProject>(k: K, v: DraftProject[K]) => {
    setDraft((d) => ({ ...d, [k]: v }));
    setDirty(true);
  };

  const formState = save.isPending ? 'saving' : dirty ? 'unsaved' : 'saved';

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/10" onClick={onClose} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${project.name}`}
        className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[520px] bg-white border-l border-neutral-200 shadow-xl flex flex-col"
      >
        <header className="px-4 py-3 border-b border-neutral-200 flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-base font-semibold text-neutral-900">Edit project</h2>
            <p className="text-[11px] text-neutral-500">{project.partner_name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-neutral-300 px-2 py-1 text-xs hover:bg-neutral-50"
            aria-label="Close project editor"
          >Close <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded ml-1">Esc</kbd></button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <label className="block">
            <span className="block text-xs text-neutral-700 mb-1">Project name <span className="text-rose-700">*</span></span>
            <input
              type="text"
              name="name"
              value={draft.name}
              onChange={(e) => update('name', e.target.value)}
              className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-neutral-700 mb-1">Description</span>
            <textarea
              value={draft.description}
              onChange={(e) => update('description', e.target.value)}
              rows={4}
              className="w-full border border-neutral-300 rounded-md px-2 py-1.5 text-sm leading-relaxed"
            />
            <span className="block text-[11px] text-neutral-500 mt-1">Drives the matching engine. Editing this triggers a re-embed — refresh matches after saving.</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs text-neutral-700 mb-1">Ask (CAD)</span>
              <div className="flex items-center gap-1">
                <span className="text-neutral-500 text-sm">$</span>
                <input
                  type="number"
                  min={0}
                  value={draft.ask_cad ?? ''}
                  onChange={(e) => update('ask_cad', e.target.value === '' ? null : Number(e.target.value))}
                  className="flex-1 border border-neutral-300 rounded-md px-2 py-1.5 text-sm tabular-nums"
                />
              </div>
            </label>
            <label className="block">
              <span className="block text-xs text-neutral-700 mb-1">Match available (CAD)</span>
              <div className="flex items-center gap-1">
                <span className="text-neutral-500 text-sm">$</span>
                <input
                  type="number"
                  min={0}
                  value={draft.match_available_cad ?? ''}
                  onChange={(e) => update('match_available_cad', e.target.value === '' ? null : Number(e.target.value))}
                  className="flex-1 border border-neutral-300 rounded-md px-2 py-1.5 text-sm tabular-nums"
                />
              </div>
            </label>
          </div>
          <label className="block">
            <span className="block text-xs text-neutral-700 mb-1">Target funding date</span>
            <input
              type="date"
              value={draft.target_funding_date ? new Date(draft.target_funding_date).toISOString().slice(0, 10) : ''}
              onChange={(e) => {
                if (!e.target.value) return update('target_funding_date', null);
                const [y, m, d] = e.target.value.split('-').map(Number);
                update('target_funding_date', Date.UTC(y, m - 1, d, 23, 59, 59));
              }}
              className="border border-neutral-300 rounded-md px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block">
            <span className="block text-xs text-neutral-700 mb-1">Status</span>
            <select
              value={draft.status}
              onChange={(e) => update('status', e.target.value as ProjectStatus)}
              className="border border-neutral-300 rounded-md px-2 py-1.5 text-sm bg-white"
            >
              {(['planning', 'seeking', 'funded', 'complete', 'cancelled'] as const).map((s) => (
                <option key={s} value={s}>{projectStatusMeta[s].label}</option>
              ))}
            </select>
          </label>
        </div>

        <footer className="px-4 py-3 border-t border-neutral-200 flex items-center justify-between shrink-0 bg-neutral-50/60">
          <span className="text-[11px] text-neutral-600" aria-live="polite">
            {formState === 'saved' && 'Saved'}
            {formState === 'unsaved' && 'Unsaved changes'}
            {formState === 'saving' && 'Saving…'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
            >Cancel</button>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
              className="rounded-md bg-accent text-white px-3 py-1.5 text-sm disabled:opacity-50 hover:bg-accent-hover flex items-center gap-2"
            >
              <span>{save.isPending ? 'Saving…' : 'Save'}</span>
              <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">⌘⏎</kbd>
            </button>
          </div>
        </footer>
      </div>
    </>
  );
}
